type QueuedRequest<T> = {
  id: string;
  fn: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
  timestamp: number;
  timeoutMs: number;
};

type Subscriber = (pendingCount: number) => void;

type EnqueueOptions = {
  id?: string;
  timeoutMs?: number;
};

export class QueueTimeoutError extends Error {
  constructor(message: string, public readonly requestId: string) {
    super(message);
    this.name = 'QueueTimeoutError';
  }
}

export class QueueCancelledError extends Error {
  constructor(message: string, public readonly requestId: string) {
    super(message);
    this.name = 'QueueCancelledError';
  }
}

export class PositionChangeQueue {
  private queue: QueuedRequest<unknown>[] = [];
  private isProcessing = false;
  private currentRequest: QueuedRequest<unknown> | null = null;
  private readonly subscribers: Set<Subscriber> = new Set();
  private requestIdCounter = 0;
  private readonly DEFAULT_TIMEOUT_MS = 30000;

  async addRequest<T>(
    fn: () => Promise<T>,
    options: EnqueueOptions = {}
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const request: QueuedRequest<T> = {
        id: options.id ?? `__auto__${++this.requestIdCounter}`,
        fn,
        resolve,
        reject,
        timestamp: Date.now(),
        timeoutMs: options.timeoutMs ?? this.DEFAULT_TIMEOUT_MS
      };

      this.queue.push(request);
      this.notifySubscribers();
      this.processQueue().catch(error => {
        console.error('PositionChangeQueue: processQueue error', error);
      });
    });
  }

  cancelMatchingRequests(
    predicate: (request: QueuedRequest<unknown>) => boolean
  ): number {
    let cancelledCount = 0;

    const retained = this.queue.filter(request => {
      if (predicate(request)) {
        request.reject(
          new QueueCancelledError('Request cancelled: obsolete', request.id)
        );
        cancelledCount++;
        return false;
      }
      return true;
    });

    this.queue = retained;

    if (this.currentRequest && predicate(this.currentRequest)) {
      console.warn(
        `PositionChangeQueue: Cannot cancel currently executing request (id: ${this.currentRequest.id})`
      );
    }

    this.notifySubscribers();
    return cancelledCount;
  }

  hasPendingRequests(): boolean {
    return this.getPendingCount() > 0;
  }

  getPendingCount(): number {
    return this.queue.length + (this.currentRequest ? 1 : 0);
  }

  clear(): void {
    this.queue.forEach(request => {
      request.reject(
        new QueueCancelledError('Queue cleared: request abandoned', request.id)
      );
    });
    this.queue = [];

    if (this.currentRequest) {
      console.warn(
        `PositionChangeQueue: Cannot cancel currently executing request during clear() (id: ${this.currentRequest.id})`
      );
    }

    this.notifySubscribers();
  }

  subscribe(subscriber: Subscriber): () => void {
    this.subscribers.add(subscriber);
    subscriber(this.getPendingCount());
    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.notifySubscribers();

    try {
      while (this.queue.length > 0) {
        const request = this.queue.shift();
        if (!request) {
          break;
        }

        await this.executeRequest(request);
      }
    } finally {
      this.isProcessing = false;
      this.notifySubscribers();
    }
  }

  private async executeRequest<T>(request: QueuedRequest<T>): Promise<void> {
    this.currentRequest = request;
    this.notifySubscribers();

    let timeoutId: NodeJS.Timeout | undefined;

    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(
            new QueueTimeoutError(
              `Request timeout after ${request.timeoutMs}ms`,
              request.id
            )
          );
        }, request.timeoutMs);
      });

      const result = await Promise.race([request.fn(), timeoutPromise]);
      request.resolve(result as T);
    } catch (error) {
      request.reject(error);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      this.currentRequest = null;
      this.notifySubscribers();
    }
  }

  private notifySubscribers(): void {
    const pendingCount = this.getPendingCount();
    this.subscribers.forEach(subscriber => subscriber(pendingCount));
  }
}

export const positionChangeQueue = new PositionChangeQueue();
